import { STATUSES_HIDDEN_FROM_NON_LIBERTY } from '../constants'
import { lookupConstantLabel } from '@/features/resident/utils/residentListConstants'
import { displayReimbursementDocumentStatus } from './reimbursementDocumentStatus'

/**
 * Reimbursement status display helpers — mirror Angular default-list role-based label rewrites.
 */

/**
 * Non-liberty users see EXTERNAL_REVIEW / PAYMENT_PENDING / RE_OPENED collapsed to "Liberty Review";
 * "External Review" always renders as "Level 3 Review" for liberty users.
 */
export function displayClaimStatus(
  raw: string,
  isLiberty: boolean,
  claimStatusLabels?: Record<string, string>,
): string {
  if (!raw) {
    return claimStatusLabels?.NOT_STARTED ?? 'Not Started'
  }

  const resolved = lookupConstantLabel(claimStatusLabels, raw, raw)

  if (!isLiberty && STATUSES_HIDDEN_FROM_NON_LIBERTY.includes(resolved)) {
    return claimStatusLabels?.LIBERTY_REVIEW ?? 'Liberty Review'
  }
  if (isLiberty && resolved === 'External Review') return 'Level 3 Review'
  return resolved
}

export function displayDocumentStatus(raw: string): string {
  if (raw == null || String(raw).trim() === '') return ''
  return displayReimbursementDocumentStatus(raw)
}

/** Normalizes document-status detail arrays for `DocumentStatusInfoIcon` (Angular parity). */
export function compactDocumentStatusList(value: unknown, hideMoveInDateHint = false): string[] {
  if (!Array.isArray(value)) return []
  return value
    .map((entry) => String(entry ?? '').trim())
    .filter((entry) =>
      hideMoveInDateHint ? entry.length > 0 && entry !== 'Select Move-in Date of New Resident' : entry.length > 0,
    )
}

export function padApplicantId(id: string | number): string {
  return String(id ?? '').padStart(8, '0')
}

/**
 * Document-card theme for the Required Documentation section: a status-colored top
 * bar plus the matching pill badge (RECEIVED → green, UPLOADED/PENDING → amber,
 * INCOMPLETE/REJECTED/MISSING → red, otherwise neutral).
 */
export function getDocumentStatusTheme(status: string): { topBar: string; badge: string } {
  const normalized = String(status ?? '').trim().toLowerCase()
  const badgeBase =
    'inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold tracking-wider uppercase whitespace-nowrap'
  switch (normalized) {
    case 'received':
    case 'approved':
    case 'complete':
    case 'completed':
    case 'paid':
      return {
        topBar: 'border-t-emerald-500',
        badge: `${badgeBase} border border-emerald-200 bg-emerald-50 text-emerald-700`,
      }
    case 'uploaded':
    case 'pending':
      return {
        topBar: 'border-t-amber-400',
        badge: `${badgeBase} border border-amber-200 bg-amber-50 text-amber-700`,
      }
    case 'incomplete':
    case 'rejected':
    case 'missing':
    case 'denied':
      return {
        topBar: 'border-t-red-500',
        badge: `${badgeBase} border border-red-200 bg-red-50 text-red-600`,
      }
    default:
      return {
        topBar: 'border-t-slate-200',
        badge: `${badgeBase} border border-slate-200 bg-slate-100 text-slate-600`,
      }
  }
}

/** Returns the matching Tailwind badge classes for a given progress or claim status. */
export function getStatusBadgeClass(status: string): string {
  const normalized = String(status ?? '').trim().toLowerCase()
  const base = 'inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-semibold border whitespace-nowrap'
  switch (normalized) {
    case 'open':
    case 'info needed':
      return `${base} bg-amber-100 border-amber-300 text-amber-800`
    case 'awaiting final info':
    case 'awaiting new resident':
      return `${base} bg-purple-50 border-purple-300 text-purple-800`
    case 'action needed':
    case 'in progress':
      return `${base} bg-blue-50 border-blue-300 text-blue-800`
    case 'ready for review':
      return `${base} bg-orange-100 border-orange-300 text-orange-800`
    case 'submitted':
    case 'in review':
    case 'liberty review':
    case 'under review':
    case 'level 3 review':
    case 'external review':
      return `${base} bg-indigo-100 border-indigo-300 text-indigo-800`
    case 'payment pending':
      return `${base} bg-orange-100 border-orange-300 text-orange-800`
    case 'denied':
      return `${base} bg-red-100 border-red-300 text-red-800`
    case 'cancelled':
    case 'expired':
      return `${base} bg-gray-100 border-gray-300 text-gray-800`
    case 'concluded':
    case 'completed':
    case 'paid':
      return `${base} bg-green-100 border-green-300 text-green-800`
    default:
      return `${base} bg-slate-50 border-slate-200 text-slate-700`
  }
}
